import PropTypes from "prop-types";
import React, { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  AttributionControl,
  MapContainer,
  Marker,
  Popup,
  TileLayer,
  useMap,
} from "react-leaflet";
import { Link } from "react-router-dom";
import { useShallow } from "zustand/react/shallow";
import RouteLink, { ROUTE_LINK_VARIANT } from "../../components/misc/RouteLink";
import {
  DEFAULT_CENTER,
  DEFAULT_ZOOM,
  OSM_ATTRIBUTION,
  OSM_TILE_URL,
  getColoredIcon,
} from "../../components/map/leaflet";
import { useNominatimStore } from "../../stores/nominatim";
import { useWorkOrdersStore } from "../../stores/work_orders";
import { formatDate } from "../../utils/dateFormat";
import { formatRange } from "../../utils/rangeFormat";
import { DUE_STATE, groupMapItemsByLocation } from "./utils";
import "../../scss/map.scss";

// Mirrors the list rows; orange instead of the pale table-warning yellow so
// due pins stay visible on the map tiles.
const DUE_STATE_COLOR = Object.freeze({
  [DUE_STATE.OVERDUE]: "#dc3545",
  [DUE_STATE.DUE]: "#fd7e14",
  [DUE_STATE.NONE]: "#0d6efd",
});

const LEGEND = [
  { dueState: DUE_STATE.OVERDUE, label: "work_orders.overdue" },
  { dueState: DUE_STATE.DUE, label: "work_orders.due" },
  { dueState: DUE_STATE.NONE, label: "work_orders.map_legend_other" },
];

const MAP_STYLE = Object.freeze({ height: "100%", width: "100%" });
const LOADING_OVERLAY_STYLE = Object.freeze({ background: "rgba(255,255,255,0.5)", zIndex: 500 });
const LEGEND_SWATCH_STYLE = Object.freeze({
  display: "inline-block",
  width: 10,
  height: 10,
  borderRadius: "50%",
  border: "1px solid #fff",
  boxShadow: "0 0 2px rgba(0,0,0,0.3)",
});
const FIT_BOUNDS_OPTIONS = Object.freeze({ padding: [32, 32], maxZoom: 15 });
const POPUP_MAX_HEIGHT = 280;

function FitBounds({ locations }) {
  const map = useMap();
  useEffect(() => {
    if (locations.length === 0) return;
    map.fitBounds(
      locations.map((location) => [location.lat, location.lon]),
      FIT_BOUNDS_OPTIONS,
    );
  }, [locations, map]);
  return null;
}

FitBounds.propTypes = {
  locations: PropTypes.arrayOf(
    PropTypes.shape({ lat: PropTypes.number.isRequired, lon: PropTypes.number.isRequired }),
  ).isRequired,
};

const workOrderMapItemShape = PropTypes.shape({
  id: PropTypes.number.isRequired,
  number: PropTypes.string.isRequired,
  scheduled_from: PropTypes.string,
  scheduled_until: PropTypes.string,
  customer_number: PropTypes.string,
  customer_name: PropTypes.string,
  property_name: PropTypes.string,
  assigned_to: PropTypes.shape({ name: PropTypes.string, number: PropTypes.string }),
});

function WorkOrderPopupEntry({ workOrder, isBackoffice }) {
  const { t } = useTranslation();
  const scheduled = formatRange({
    from: formatDate(workOrder.scheduled_from),
    until: formatDate(workOrder.scheduled_until),
    untilPrefix: t("common.range_until"),
  });
  const detailsUrl = `/work_orders/${workOrder.id}/details`;
  const customer = [workOrder.customer_number, workOrder.customer_name].filter(Boolean).join(" · ");
  const assignee = workOrder.assigned_to
    ? [workOrder.assigned_to.name, workOrder.assigned_to.number && `(${workOrder.assigned_to.number})`]
        .filter(Boolean)
        .join(" ")
    : t("work_orders.unassigned");

  return (
    <li className="mt-2">
      <Link to={detailsUrl} className="fw-bold">
        {workOrder.number}
      </Link>
      {scheduled && <span className="text-muted"> · {scheduled}</span>}
      {customer && <div>{customer}</div>}
      {workOrder.property_name && <div>{workOrder.property_name}</div>}
      {isBackoffice && (
        <div className="text-muted">
          {t("work_orders.assigned_to")}: {assignee}
        </div>
      )}
    </li>
  );
}

WorkOrderPopupEntry.propTypes = {
  workOrder: workOrderMapItemShape.isRequired,
  isBackoffice: PropTypes.bool.isRequired,
};

function WorkOrderLocationMarker({ location, isBackoffice }) {
  const { t } = useTranslation();
  const position = [location.lat, location.lon];
  const icon = getColoredIcon(DUE_STATE_COLOR[location.dueState]);
  // With several work orders at one address, each entry links its own details.
  const detailsUrl =
    location.workOrders.length === 1 ? `/work_orders/${location.workOrders[0].id}/details` : null;

  return (
    <Marker position={position} icon={icon}>
      <Popup maxHeight={POPUP_MAX_HEIGHT}>
        <div className="small">
          <div className="fw-bold">{location.address}</div>
          <ul className="list-unstyled mb-0">
            {location.workOrders.map((workOrder) => (
              <WorkOrderPopupEntry key={workOrder.id} workOrder={workOrder} isBackoffice={isBackoffice} />
            ))}
          </ul>
          <div className="btn-group w-100 mt-2" role="group">
            {detailsUrl && (
              <Link to={detailsUrl} className="btn btn-default btn-sm">
                <i className="fa fa-magnifying-glass fa-fw me-1" />
                {t("common.details")}
              </Link>
            )}
            <RouteLink address={location.address} variant={ROUTE_LINK_VARIANT.BUTTON} />
          </div>
        </div>
      </Popup>
    </Marker>
  );
}

WorkOrderLocationMarker.propTypes = {
  location: PropTypes.shape({
    lat: PropTypes.number.isRequired,
    lon: PropTypes.number.isRequired,
    address: PropTypes.string.isRequired,
    dueState: PropTypes.oneOf(Object.values(DUE_STATE)).isRequired,
    workOrders: PropTypes.arrayOf(workOrderMapItemShape).isRequired,
  }).isRequired,
  isBackoffice: PropTypes.bool.isRequired,
};

function LegendEntry({ dueState, label }) {
  const { t } = useTranslation();
  const swatchStyle = { ...LEGEND_SWATCH_STYLE, background: DUE_STATE_COLOR[dueState] };

  return (
    <li className="list-inline-item">
      <span aria-hidden="true" className="me-1" style={swatchStyle} />
      {t(label)}
    </li>
  );
}

LegendEntry.propTypes = {
  dueState: PropTypes.oneOf(Object.values(DUE_STATE)).isRequired,
  label: PropTypes.string.isRequired,
};

function WorkOrdersMap({ q, filter, due, assignedToId, zipPrefixes, isBackoffice }) {
  const { t } = useTranslation();
  const { mapItems, mapStats, mapLoading, fetchMap } = useWorkOrdersStore(
    useShallow((state) => ({
      mapItems: state.mapItems,
      mapStats: state.mapStats,
      mapLoading: state.mapLoading,
      fetchMap: state.fetchMap,
    })),
  );
  const nominatimAvailable = useNominatimStore((state) => state.available);

  useEffect(() => {
    useNominatimStore.getState().fetchHealth();
  }, []);

  useEffect(() => {
    fetchMap({ q, filter, due, assigned_to_id: assignedToId, zip_prefix: zipPrefixes });
  }, [assignedToId, due, fetchMap, filter, q, zipPrefixes]);

  const locations = useMemo(() => groupMapItemsByLocation(mapItems), [mapItems]);
  const shown = mapStats.located + mapStats.unlocated;
  const isTruncated = mapStats.total > shown;
  const isEmpty = !mapLoading && mapStats.total === 0;

  return (
    <div>
      {nominatimAvailable === false && (
        <div className="alert alert-warning mb-3">
          <i className="fa fa-exclamation-triangle me-2" />
          {t("nominatim.unavailable")}
        </div>
      )}

      {isTruncated && (
        <div className="alert alert-info mb-3">
          {t("work_orders.map_truncated", { shown, total: mapStats.total })}
        </div>
      )}

      <div className="map-wrapper">
        <MapContainer
          center={DEFAULT_CENTER}
          zoom={DEFAULT_ZOOM}
          scrollWheelZoom
          attributionControl={false}
          style={MAP_STYLE}
        >
          <AttributionControl prefix={false} />
          <TileLayer attribution={OSM_ATTRIBUTION} url={OSM_TILE_URL} />
          <FitBounds locations={locations} />
          {locations.map((location) => (
            <WorkOrderLocationMarker key={location.key} location={location} isBackoffice={isBackoffice} />
          ))}
        </MapContainer>

        {mapLoading && (
          <div
            className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
            style={LOADING_OVERLAY_STYLE}
          >
            <div className="spinner-border" role="status">
              <span className="visually-hidden">{t("work_orders.map_loading")}</span>
            </div>
          </div>
        )}
      </div>

      <ul className="list-inline small text-muted mb-1" aria-label={t("work_orders.map_legend")}>
        {LEGEND.map((entry) => (
          <LegendEntry key={entry.dueState} dueState={entry.dueState} label={entry.label} />
        ))}
      </ul>

      {isEmpty ? (
        <div className="text-muted small">{t("work_orders.map_empty")}</div>
      ) : (
        <div className="text-muted small">
          {t("work_orders.map_stats", {
            located: mapStats.located,
            shown,
            unlocated: mapStats.unlocated,
          })}
        </div>
      )}
    </div>
  );
}

WorkOrdersMap.propTypes = {
  q: PropTypes.string.isRequired,
  filter: PropTypes.string.isRequired,
  due: PropTypes.string.isRequired,
  assignedToId: PropTypes.string.isRequired,
  zipPrefixes: PropTypes.arrayOf(PropTypes.string).isRequired,
  isBackoffice: PropTypes.bool.isRequired,
};

export default WorkOrdersMap;
